import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, CheckCircle2, MessageCircle, Download, Loader2,
  FlaskConical, Droplets, Beaker, Package, AlertTriangle, Phone,
} from "lucide-react";
import ImageWithFallback from "@/components/ImageWithFallback";
import ProductCard from "@/components/ProductCard";
import EnquiryDialog from "@/components/EnquiryDialog";
import SectionHeading from "@/components/SectionHeading";
import { getProduct, getRelated } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { productWhatsapp, telLink } from "@/lib/whatsapp";
import { IMAGES } from "@/lib/brand";

function InfoBlock({ icon: Icon, title, children }) {
  return (
    <div className="biosyn-card p-6">
      <div className="flex items-center gap-2.5 mb-3">
        <Icon className="w-5 h-5" style={{ color: "#087FBF" }} />
        <h3 className="font-heading font-bold text-base uppercase tracking-wide" style={{ color: "#063B63" }}>{title}</h3>
      </div>
      <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#4A6B82" }}>{children}</div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState("");

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        setActiveImg(p.image || IMAGES.placeholder);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    getRelated(slug).then(setRelated).catch(() => setRelated([]));
  }, [slug]);

  useSeo({
    title: product ? (product.seo_title || `${product.name} | BIOSYN`) : "Product | BIOSYN",
    description: product ? (product.seo_description || product.short_description) : "",
    image: product?.image,
    jsonLd: product ? {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.short_description,
      category: product.category_name,
      sku: product.code,
      image: product.image,
      brand: { "@type": "Brand", name: "BIOSYN" },
    } : undefined,
  });

  if (loading) {
    return <div className="container-x py-40 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#087FBF" }} /></div>;
  }

  if (notFound || !product) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="font-heading text-3xl font-bold" style={{ color: "#063B63" }}>Product not found</h1>
        <Link to="/products" className="btn-primary mt-6 inline-flex">Back to Products</Link>
      </div>
    );
  }

  const gallery = (product.gallery && product.gallery.length ? product.gallery : [product.image]).filter(Boolean);

  return (
    <div>
      {/* Breadcrumb */}
      <div className="container-x pt-8">
        <div className="flex items-center gap-2 text-sm" style={{ color: "#4A6B82" }}>
          <Link to="/products" className="hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Products</Link>
          <span>/</span>
          <span style={{ color: "#063B63" }} className="font-semibold">{product.name}</span>
        </div>
      </div>

      {/* Main */}
      <section className="container-x py-10">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div>
            <div className="biosyn-card !p-0 overflow-hidden" style={{ background: "#F5FAFC" }}>
              <div className="aspect-square flex items-center justify-center p-8">
                <ImageWithFallback src={activeImg} alt={`${product.name} — ${product.category_name}`} data-testid="product-main-image" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
            {gallery.length > 1 && (
              <div className="mt-4 flex gap-3">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActiveImg(g)} data-testid={`gallery-thumb-${i}`}
                    className="w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors"
                    style={{ borderColor: activeImg === g ? "#16B8D4" : "rgba(8,127,191,0.15)", background: "#fff" }}>
                    <ImageWithFallback src={g} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-contain p-1.5" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <p className="overline-label mb-2">{product.category_name}</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: "#063B63" }}>{product.name}</h1>
            {product.tagline && (
              <p className="mt-3 font-semibold text-sm md:text-base tracking-wide" style={{ color: "#16B8D4" }}>{product.tagline}</p>
            )}
            <p className="mt-5 text-base leading-relaxed" style={{ color: "#4A6B82" }}>{product.description}</p>

            {product.presentation && (
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold" style={{ background: "#F5FAFC", color: "#17324D" }}>
                <Package className="w-4 h-4" /> Presentation: {product.presentation}
              </div>
            )}

            {product.benefits?.length > 0 && (
              <div className="mt-7">
                <h3 className="font-heading font-bold text-base uppercase tracking-wide mb-3" style={{ color: "#063B63" }}>Key Benefits</h3>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {product.benefits.map((b) => (
                    <div key={b} className="flex items-start gap-2.5 text-sm" style={{ color: "#17324D" }}>
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#3FAE35" }} />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <EnquiryDialog
                productName={product.name}
                productId={product.id}
                source="PRODUCT_ENQUIRY"
                title="Request Product Information"
                trigger={<button data-testid="detail-request-info" className="btn-primary flex-1">Request Product Information <ArrowRight className="w-4 h-4" /></button>}
              />
              <a href={productWhatsapp(product.name)} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp" className="btn-green">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" data-testid="detail-expert" className="btn-outline-blue flex-1">Talk to Technical Expert</Link>
              {product.brochure_url && (
                <a href={product.brochure_url} target="_blank" rel="noopener noreferrer" data-testid="detail-brochure" className="btn-outline-blue">
                  <Download className="w-4 h-4" /> Download Brochure
                </a>
              )}
            </div>

            <div className="mt-6 flex items-start gap-2.5 p-4 rounded-xl" style={{ background: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.25)" }}>
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#c98a00" }} />
              <p className="text-xs font-bold tracking-wide" style={{ color: "#8a6400" }}>{product.important_note}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Details grid */}
      <section className="section grain" style={{ background: "#FFFFFF" }}>
        <div className="container-x grid md:grid-cols-2 gap-6">
          {product.technical_highlights?.length > 0 && (
            <InfoBlock icon={FlaskConical} title="Technical Highlights">
              <ul className="space-y-2">
                {product.technical_highlights.map((t) => (
                  <li key={t} className="flex items-start gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#16B8D4" }} />{t}</li>
                ))}
              </ul>
            </InfoBlock>
          )}
          {product.composition && <InfoBlock icon={Beaker} title="Composition">{product.composition}</InfoBlock>}
          {product.dosage && <InfoBlock icon={Droplets} title="Dosage">{product.dosage}</InfoBlock>}
          {product.application && <InfoBlock icon={Droplets} title="Application">{product.application}</InfoBlock>}
          {product.storage && <InfoBlock icon={Package} title="Storage">{product.storage}</InfoBlock>}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="section container-x">
          <SectionHeading overline="Related Range" title="You May Also Be Interested In" />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
